import React, { useState, useMemo } from "react";
import Sidebar from "../../components/layout/Sidebar/Sidebar.tsx";
import Header from "../../components/layout/Header/Header.tsx";
import AddNewSiteModal from "../../components/Modals/AddNewSiteModal.tsx";
import EditNewSiteModal from "../../components/Modals/EditNewSiteModal.tsx";
import CustomDropdown from "../../components/CustomDropdown/CustomDropdown.tsx";
import { Box } from "@mui/material";
import GenericComplexTable from "../../components/GenericComplexTable/GenericComplexTable.tsx";
import { usePortfolioTable } from "../../utils/api/usePortfolioTable.ts";
import ConfirmDeleteModal from "../../components/Modals/ConfirmDeleteModal.tsx";
import useScreenSize from "../../utils/hooks/useScreenSize.ts";
import { useAuth } from "../../context/auth.context.tsx";
import AddNewPortfolioModal from "../../components/Modals/AddNewPortfolioModal.tsx";
import { portfolioTableColumns } from "./utils/TablesOptons.tsx";
import { usePortfolios } from "../../utils/api/usePortfolios.ts";
import CustomButton from "../../components/Basics/CustomButton/CustomButton.tsx";

const Portfolios: React.FC = () => {
  const screenSize = useScreenSize();
  const { session, logOutAction } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAddPortfolioModalOpen, setIsAddPortfolioModalOpen] = useState(false);
  const [selectedPortfolios, setSelectedPortfolios] = useState<string[]>([]);
  const [siteId, setSiteId] = useState<any | null>(null);

  const {
    data: PortfoliosData,
    isLoading: isLoadingPortfoliosData,
    isError: isErrorPortfoliosData,
  } = usePortfolios(session);

  const selectedPortfolioIds = useMemo(() => {
    if (!PortfoliosData || selectedPortfolios.length === 0) {
      return [];
    }

    return selectedPortfolios
      .map((name) => PortfoliosData.find((portfolio) => portfolio.name === name)?.id)
      .filter((id): id is number => id !== undefined);
  }, [PortfoliosData, selectedPortfolios]);

  const {
    data: portfolioTableData,
    isLoading,
    isFetching,
    isError,
  } = usePortfolioTable(selectedPortfolioIds, selectedPortfolios.length > 0);

  const updateUrlWithSelection = (portfolios: string[]) => {
    if (portfolios.length > 0) {
      const portfolioIds = portfolios
        .map((name) => PortfoliosData?.find((p) => p.name === name)?.id)
        .filter((id) => id !== undefined);
      
      const queryString = `?selected=${portfolioIds.join(',')}`;
      window.history.replaceState(
        {},
        '',
        `${window.location.pathname}${queryString}`
      );
    } else {
      window.history.replaceState(
        {},
        '',
        window.location.pathname
      );
    }
  };

  React.useEffect(() => {
    if (!PortfoliosData) return;

    const url = window.location.href;
    const selectedMatch = url.match(/[?&]selected=([^&]*)/);
    
    if (selectedMatch && selectedMatch[1]) {
      const selectedIds = selectedMatch[1].split(',').map(id => parseInt(id));
      const selectedNames = PortfoliosData
        .filter(portfolio => selectedIds.includes(portfolio.id))
        .map(portfolio => portfolio.name);
      
      setSelectedPortfolios(selectedNames);
    }
  }, [PortfoliosData]);

  const handleDropdownChange = (newValue: string[]) => {
    setSelectedPortfolios(newValue);
    updateUrlWithSelection(newValue);
  };

  const filteredTableData = useMemo(() => portfolioTableData ?? [], [portfolioTableData]);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleOpenEditModal = (row: any) => {
    const idFromUrl = extractIdFromUrl(row.url);
    setSiteId(idFromUrl);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSiteId(null);
  };

  const extractIdFromUrl = (url: string): any => {
    return url.match(/\/sites\/(\d+)\//)?.[1];
  };

  const handleDeleteRow = (row: any) => {
    const idFromUrl = extractIdFromUrl(row.url);
    setSiteId(idFromUrl);
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setSiteId(null);
  };

  const handleOpenAddPortfolioModal = () => {
    setIsAddPortfolioModalOpen(true);
  };

  const handleCloseAddPortfolioModal = () => {
    setIsAddPortfolioModalOpen(false);
  };

  return (
    <div style={{ display: "flex", height: "100vh" }}>
      <Sidebar />
      <div style={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
        <Header text="Portfolios" onLogout={logOutAction}>
          <CustomButton
            sx={{ mr: 1 }}
            variant="contained"
            color="primary"
            label="Add a New Portfolio"
            onClick={handleOpenAddPortfolioModal}
          />
        </Header>

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          m={2}
          p={2}
          bgcolor="#F5F5F5"
          borderRadius="4px"
          width="50%"
        >
          <CustomDropdown
            Title="Select Portfolio"
            Options={(PortfoliosData || []).map((portfolio) => portfolio.name)}
            onChangeValue={handleDropdownChange}
            selectedValues={selectedPortfolios}
          />

          <CustomButton
            sx={{
              paddingY: 0.5,
            }}
            variant="outlined"
            color="secondary"
            label=" Add a New Site"
            onClick={handleOpenModal}
          />
        </Box>

        <Box m={2}>
          <GenericComplexTable
            title="Sites"
            tableData={filteredTableData ? filteredTableData : []}
            isLoading={isLoading || isLoadingPortfoliosData}
            isFetching={isFetching}
            isError={isError || isErrorPortfoliosData}
            columns={portfolioTableColumns}
            maxHeight={"400px"}
            maxWidth={screenSize.width - 180}
            enableExport={true}
            enableBottomToolbar={true}
            onEditRow={handleOpenEditModal}
            onDeleteRow={handleDeleteRow}
          />
        </Box>
      </div>
      <AddNewSiteModal open={isModalOpen} onClose={handleCloseModal} />
      {siteId && (
        <EditNewSiteModal
          open={isEditModalOpen}
          onClose={handleCloseEditModal}
          id={siteId} // Ensure this `siteId` is being correctly updated for each row
        />
      )}
      {siteId && (
        <ConfirmDeleteModal
          open={isDeleteModalOpen}
          onClose={handleCloseDeleteModal}
          id={siteId}
        />
      )}

      <AddNewPortfolioModal
        open={isAddPortfolioModalOpen}
        onClose={handleCloseAddPortfolioModal}
      />
    </div>
  );
};

export default Portfolios;
